import { inject, observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import { CHAT_MEMBER_COLOR_MAPPING } from "../_stores/chat/consts";
import TailwindIcon from "library/components/_tailwind/icon";
import BroadcastStore from "../_stores/broadcast/BroadcastStore";
import { ClickAwayListener, Popper } from "@material-ui/core";
import LayoutStore from "library/core/stores/layout/LayoutStore";
import NodeChatStore from "../_stores/nodechat/NodeChatStore";
import LanguageStore from "library/core/stores/language/LanguageStore";
import useMemberIntro from "common/member-intro/use-member-intro";
import { clearMemberCard } from "common/member-intro/load-member-intro";
import {
  memberGiftLabel,
  memberIntroFallbackPhoto,
  memberIntroGenderLabel,
} from "common/member-intro/format";
import { COUNTRY_FLAGS } from "library/core/constants";
import find from "lodash/find";
import MedalIcon from "library/components/_tailwind/icon/icons/medal-icon";
import { StreamNotePopup } from "../broadcast-stream-note-modal";
import { IMember, IMemberHash } from "../_stores/chat/interfaces";

interface Props {
  nodeChatStore?: NodeChatStore;
  broadcastStore?: BroadcastStore;
  layoutStore?: LayoutStore;
  userId?: string;
  userName?: string;
  chatTooltip?: boolean;
  onUserTierColor?: (color: string) => void;
  languageStore?: LanguageStore;
}

const memberName = (value?: string) =>
  (value || "").replace(/:$/, "").trim().toLowerCase();

const findChatMember = (
  hash: IMemberHash | undefined,
  userId?: string,
  userName?: string
): IMember | undefined => {
  if (!hash) {
    return undefined;
  }
  if (userId && hash[userId]) {
    return hash[userId];
  }
  const lowerId = userId?.toLowerCase();
  if (lowerId && hash[lowerId]) {
    return hash[lowerId];
  }
  const name = memberName(userName);
  if (!name || name === "me" || name.startsWith("[system")) {
    return undefined;
  }
  return Object.values(hash).find(item => {
    const username = memberName(item.username);
    const id = (item.id || "").toLowerCase();
    const handle = id.split(/@|\+/)[0];
    return username === name || handle === name || id === name;
  });
};

const countryLabel = (code?: string | null, language?: string) => {
  if (!code) {
    return "";
  }
  const country = find(COUNTRY_FLAGS, item => item.ISO === code.toUpperCase());
  if (!country) {
    return code;
  }
  const lang = (language || "en").split("-")[0];
  return country.country[lang] || country.country.en || code;
};

const MemberCardSilhouette: React.FC = () => (
  <svg
    className='ChatItem__MemberCardSilhouette'
    viewBox='0 0 72 72'
    aria-hidden='true'>
    <path d='M20.8,18.1c0-8.5,6.9-15.3,15.5-15.3c8.5,0,15.5,6.9,15.5,15.3v10.3c0,8.5-6.9,15.3-15.5,15.3  c-8.5,0-15.5-6.9-15.5-15.5V18.1z M0.1,64.2c2.5-7.7,20.5-15.3,30.8-15.3h10.3c10.3,0,28.3,7.7,30.8,15.3v5.1H0L0.1,64.2z' />
  </svg>
);

const ChatItemUsernameTooltip: React.FunctionComponent<Props> = ({
  children,
  userId,
  userName,
  nodeChatStore,
  broadcastStore,
  layoutStore,
  chatTooltip = true,
  onUserTierColor,
  languageStore,
}) => {
  const [nameColor, setNameColor] = useState("");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [brokenPhoto, setBrokenPhoto] = useState("");
  const [photoAttempt, setPhotoAttempt] = useState(0);
  const [noteVersion, setNoteVersion] = useState(0);
  const isPrivateConsentOpen = broadcastStore?.isPrivateConsentOpen;
  const bountyOrderMember = broadcastStore?.bountyOrderMember;
  const fanClubOrderMember = broadcastStore?.fanClubOrderMember;
  const screenSize = layoutStore?.screenSize;
  const intl = languageStore?.intl;

  const bountyOrdered = bountyOrderMember === userName;
  const fanClubOrdered = fanClubOrderMember === userName;

  const member = findChatMember(
    nodeChatStore?.allMemberHash,
    userId,
    userName
  );
  const introLookupIds = anchorEl
    ? [member?.pwsid, member?.lvs_uid, member?.user_id]
    : [];
  const { intro, isLoading: isCardLoading } = useMemberIntro(
    introLookupIds,
    noteVersion
  );
  const formatMessage = descriptor =>
    intl ? intl.formatMessage(descriptor) : descriptor.defaultMessage;
  const genderLabel = memberIntroGenderLabel(intro?.gender, formatMessage);
  const placeLabel =
    countryLabel(intro?.country, languageStore?.language) ||
    countryLabel(member?.country, languageStore?.language);
  const summaryParts = [
    placeLabel,
    genderLabel,
    intro?.age ? String(intro.age) : "",
  ].filter(Boolean);
  const summaryLine = summaryParts.join(" • ");
  const attributeLine = [intro?.ethnicity, intro?.sexual_orientation]
    .filter(Boolean)
    .join(" • ");
  const languageLine = (intro?.languages || []).filter(Boolean).join(", ");
  const detailParts = [summaryLine, attributeLine, languageLine].filter(
    Boolean
  );
  const memberPhoto = intro?.profile_picture || "";
  const fallbackPhoto = memberIntroFallbackPhoto(intro?.gender);
  const photoUrl =
    memberPhoto && brokenPhoto !== memberPhoto
      ? memberPhoto
      : fallbackPhoto && brokenPhoto !== fallbackPhoto
      ? fallbackPhoto
      : "";

  useEffect(() => {
    setBrokenPhoto("");
    setPhotoAttempt(0);
  }, [memberPhoto]);

  const handlePhotoError = () => {
    if (photoUrl && photoUrl === memberPhoto && photoAttempt < 1) {
      introLookupIds.forEach(id => clearMemberCard(id));
      setPhotoAttempt(attempt => attempt + 1);
      setNoteVersion(version => version + 1);
      return;
    }
    setBrokenPhoto(photoUrl);
  };
  const savedNote =
    !member?.pwsid || (isCardLoading && !intro)
      ? null
      : intro?.note?.note || "";
  const spendingTier = member?.sort;
  const isFanClub = intro
    ? intro.is_fanclub
    : !!member?.isFanClub || (fanClubOrdered && !member?.isFanClub);
  const isPremiere = !!member?.isPremiere;
  const hasToken = intro ? intro.has_tokens : !!member?.hasTokens;
  const isBounty = !!member?.isBounty || (bountyOrdered && !member?.isBounty);
  const isBountyTarget = !!member?.isBountyTarget && !bountyOrdered;
  const memberRank =
    intro && typeof intro.admirer_rank === "number"
      ? intro.admirer_rank
      : member?.topAdmirer || 0;
  const isTopAdmirer = memberRank >= 1 && memberRank <= 5;
  const showTooltip = !!member;

  useEffect(() => {
    const setColor = () => {
      if (isTopAdmirer) {
        setNameColor(CHAT_MEMBER_COLOR_MAPPING.TOP_ADMIRER);
      } else if (isBounty) {
        setNameColor(CHAT_MEMBER_COLOR_MAPPING.BOUNTY_COLOR_DEFAULT);
      } else if (isBountyTarget) {
        setNameColor(CHAT_MEMBER_COLOR_MAPPING.DEFAULT_COLOR_DEFAULT);
      } else if (isPremiere && !isFanClub) {
        setNameColor(CHAT_MEMBER_COLOR_MAPPING.PREM_COLOR_DEFAULT);
      } else if (isFanClub && !isPremiere) {
        setNameColor(CHAT_MEMBER_COLOR_MAPPING.FAN_COLOR_DEFAULT);
      } else if (isFanClub && isPremiere) {
        setNameColor(CHAT_MEMBER_COLOR_MAPPING.FAN_COLOR_DEFAULT);
      } else if (spendingTier && spendingTier >= 0.8) {
        setNameColor(CHAT_MEMBER_COLOR_MAPPING.SPENT_TIER_THREE);
      } else if (spendingTier && spendingTier >= 0.6) {
        setNameColor(CHAT_MEMBER_COLOR_MAPPING.SPENT_TIER_TWO);
      } else if (spendingTier && spendingTier >= 0.4) {
        setNameColor(CHAT_MEMBER_COLOR_MAPPING.SPENT_TIER_ONE);
      } else if (hasToken) {
        setNameColor(CHAT_MEMBER_COLOR_MAPPING.PAID_COLOR_DEFAULT);
      } else {
        setNameColor(CHAT_MEMBER_COLOR_MAPPING.DEFAULT_COLOR_DEFAULT);
      }
    };
    setColor();
  }, [
    isFanClub,
    isPremiere,
    hasToken,
    isBounty,
    isBountyTarget,
    spendingTier,
    isTopAdmirer,
    bountyOrderMember,
    bountyOrdered,
  ]);

  useEffect(() => {
    if (nameColor) {
      onUserTierColor?.(nameColor);
    }
  }, [nameColor]);

  const hideTooltip = useCallback(() => {
    setAnchorEl(null);
    setIsExpanded(false);
    setIsEditorOpen(false);
  }, []);

  useEffect(() => {
    if (isPrivateConsentOpen) {
      setAnchorEl(null);
      setIsExpanded(false);
      setIsEditorOpen(false);
    }
  }, [isPrivateConsentOpen]);

  useEffect(() => {
    setAnchorEl(null);
    setIsExpanded(false);
    setIsEditorOpen(false);
  }, [screenSize]);

  const handleTooltipClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const openEditor = () => {
    setIsExpanded(true);
    setIsEditorOpen(true);
  };

  const photo = (
    <div className='ChatItem__MemberCardPhoto'>
      {photoUrl ? (
        <img
          key={`${photoUrl}-${photoAttempt}`}
          src={photoUrl}
          alt=''
          referrerPolicy='no-referrer'
          onError={handlePhotoError}
        />
      ) : (
        <MemberCardSilhouette />
      )}
    </div>
  );

  const rankIcons = (placement: "inline" | "aside") => {
    const iconSize = placement === "aside" ? 22 : 16;
    const iconState = (active: boolean) =>
      active
        ? "ChatItem__MemberCardIcon ChatItem__MemberCardIcon--active"
        : "ChatItem__MemberCardIcon ChatItem__MemberCardIcon--inactive";
    return (
      <div
        className={
          placement === "aside"
            ? "ChatItem__MemberCardIcons ChatItem__MemberCardIcons--aside"
            : "ChatItem__MemberCardIcons ChatItem__MemberCardIcons--inline"
        }>
        <span
          className={iconState(hasToken)}
          style={{ color: hasToken ? "#d4af37" : undefined }}
          title={intl?.formatMessage({
            id: hasToken
              ? "broadcast.chatItem.tooltip-member-labels.has-tokens"
              : "broadcast.chatItem.tooltip-member-labels.no-tokens",
            defaultMessage: hasToken ? "Has tokens" : "No tokens",
          })}>
          <TailwindIcon name='token' />
        </span>
        <span
          className={iconState(isFanClub)}
          style={{ color: isFanClub ? "#d4af37" : undefined }}
          title={intl?.formatMessage({
            id: isFanClub
              ? "broadcast.chatItem.tooltip-member-labels.fan-club-member"
              : "broadcast.chatItem.tooltip-member-labels.not-fan-club",
            defaultMessage: isFanClub
              ? "Fan Club Member"
              : "Not a fan club member",
          })}>
          <TailwindIcon name='star' />
        </span>
        <span
          className={iconState(isPremiere)}
          style={{ color: isPremiere ? "#d4af37" : undefined }}
          title={intl?.formatMessage({
            id: "broadcast.chatItem.tooltip-member-labels.premiere",
            defaultMessage: "Premiere",
          })}>
          <TailwindIcon name='crown' />
        </span>
        <span
          className={`ChatItem__MemberCardMedal ${iconState(isTopAdmirer)}`}
          style={{ color: isTopAdmirer ? "#d4af37" : undefined }}
          title={
            isTopAdmirer
              ? intl?.formatMessage(
                  {
                    id: "broadcast.chatItem.tooltip-member-labels.top-admirer",
                    defaultMessage: "Top Admirer #{memberRank}",
                  },
                  { memberRank }
                )
              : intl?.formatMessage({
                  id: "broadcast.chatItem.tooltip-member-labels.top-admirer-no-rank",
                  defaultMessage: "Top Admirer",
                })
          }>
          <MedalIcon
            iconRange={(isTopAdmirer ? memberRank : 1) as 1 | 2 | 3 | 4 | 5}
            style={{ width: iconSize, height: iconSize }}
          />
        </span>
      </div>
    );
  };

  if (!nodeChatStore || !broadcastStore || !layoutStore || !intl) {
    return <>{children}</>;
  }

  return showTooltip ? (
    <>
      <span
        onClick={handleTooltipClick}
        className='ChatItem__UsernameLink'
        style={{ color: nameColor }}>
        {children}
      </span>

      <Popper
        style={chatTooltip ? { marginBottom: 8 } : { marginTop: 4 }}
        placement={chatTooltip ? "top" : "bottom-start"}
        className='ChatItem__UsernameWrapper ChatItem__UsernameWrapper--card'
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}>
        <ClickAwayListener onClickAway={hideTooltip}>
          <div
            onClick={e => {
              e.stopPropagation();
            }}
            className={
              isExpanded
                ? "ChatItem__MemberCard ChatItem__MemberCard--expanded"
                : "ChatItem__MemberCard"
            }>
            <div className='ChatItem__MemberCardMain'>
              <div className='ChatItem__MemberCardBody'>
                {photo}
                {rankIcons("aside")}
                <div className='ChatItem__MemberCardCopy'>
                  <div className='ChatItem__MemberCardNameRow'>
                    <div className='ChatItem__MemberCardName'>{userName}</div>
                    {rankIcons("inline")}
                  </div>
                  {isExpanded && summaryParts.length ? (
                    <div className='ChatItem__MemberCardMeta ChatItem__MemberCardMeta--spread'>
                      {summaryParts.map((part, index) => (
                        <span key={`${part}-${index}`}>{part}</span>
                      ))}
                    </div>
                  ) : detailParts.length ? (
                    <div className='ChatItem__MemberCardMeta'>
                      {detailParts.map((part, index) => (
                        <span key={`${part}-${index}`}>{part}</span>
                      ))}
                    </div>
                  ) : null}
                  {intro?.about_me ? (
                    <div className='ChatItem__MemberCardAbout'>
                      {intro.about_me}
                    </div>
                  ) : null}
                  {isExpanded && intro?.last_gifts?.length ? (
                    <div className='ChatItem__MemberCardGifts'>
                      {intro.last_gifts.map((gift, index) => (
                        <img
                          key={`${gift.gift_id}-${gift.sent_at}-${index}`}
                          className='ChatItem__MemberCardGift'
                          src={gift.picture_name}
                          alt={memberGiftLabel(gift.name)}
                          title={memberGiftLabel(gift.name)}
                        />
                      ))}
                    </div>
                  ) : null}
                  {isExpanded && savedNote ? (
                    <div
                      className='ChatItem__MemberCardSavedNote'
                      role='button'
                      onClick={event => {
                        event.stopPropagation();
                        openEditor();
                      }}>
                      {savedNote}
                    </div>
                  ) : null}
                  {isExpanded && savedNote === "" ? (
                    <div
                      className='ChatItem__MemberCardAddNote'
                      role='button'
                      onClick={event => {
                        event.stopPropagation();
                        openEditor();
                      }}>
                      {intl?.formatMessage({
                        id: "stream-note-popup.add",
                        defaultMessage: "Add a note",
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <div className='ChatItem__MemberCardRail'>
              <div
                className='ChatItem__MemberCardClose'
                role='button'
                onClick={hideTooltip}>
                <TailwindIcon name='close' />
              </div>
              <div className='ChatItem__MemberCardNoteWrap'>
                <div
                  className='ChatItem__MemberCardNote'
                  role='button'
                  title={intl?.formatMessage({
                    id: isExpanded
                      ? "member-card.collapse"
                      : "member-card.expand",
                    defaultMessage: isExpanded ? "Collapse" : "Expand",
                  })}
                  aria-expanded={isExpanded}
                  onClick={event => {
                    event.stopPropagation();
                    setIsExpanded(open => !open);
                    setIsEditorOpen(false);
                  }}>
                  <svg
                    className='ChatItem__MemberCardNoteIcon'
                    viewBox='0 0 24 24'
                    aria-hidden='true'>
                    <rect
                      x='3.25'
                      y='4.25'
                      width='17.5'
                      height='15.5'
                      rx='2.75'
                    />
                    <path d='M3.25 9.15h17.5' />
                    <path
                      className='ChatItem__MemberCardNoteChevron'
                      d='M9.1 13.15 L12 16.05 L14.9 13.15'
                    />
                  </svg>
                </div>
                {isEditorOpen && member && (
                  <StreamNotePopup
                    member={member}
                    onClose={() => setIsEditorOpen(false)}
                    onNoteChanged={() =>
                      setNoteVersion(version => version + 1)
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </ClickAwayListener>
      </Popper>
    </>
  ) : (
    <span className='ChatItem__UsernameLink' style={{ color: nameColor }}>
      {children}
    </span>
  );
};

export default inject(
  "nodeChatStore",
  "broadcastStore",
  "layoutStore",
  "languageStore"
)(observer(ChatItemUsernameTooltip));
